<script setup>
/**
 * FeatureRow — an alternating marketing feature block: copy on one side, a
 * framed media slot (typically an <app-frame>) on the other. Set `flip` to
 * swap sides on desktop. Stacks copy-first on mobile.
 *
 * Slots: default (body copy — use <p> tags), #actions, #media.
 */
defineProps({
  eyebrow: {type: String, default: null},
  title: {type: String, required: true},
  flip: {type: Boolean, default: false},
  // Optional identity accents (the /formats hub): a small tinted icon tile
  // beside the title, and the eyebrow tinted to match. Defaults keep the
  // classic brand-red look everywhere else.
  icon: {type: String, default: null},
  accent: {type: String, default: null},
  tint: {type: String, default: null},
});
</script>

<template>
  <section class="fr">
    <v-row align="center" class="fr-row" :class="{ 'flex-md-row-reverse': flip }">
      <v-col cols="12" md="6">
        <div class="fr-copy">
          <span v-if="eyebrow" class="fr-eyebrow" :style="accent ? {color: accent} : null">{{ eyebrow }}</span>
          <div class="fr-title-row">
            <span v-if="icon" class="fr-title-ic"
                  :style="{background: tint ?? undefined, color: accent ?? undefined}">
              <v-icon :icon="icon" size="20"/>
            </span>
            <h2 class="fr-title">{{ title }}</h2>
          </div>
          <div class="fr-body">
            <slot/>
          </div>
          <div v-if="$slots.actions" class="fr-actions">
            <slot name="actions"/>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="6">
        <div class="fr-media">
          <slot name="media"/>
        </div>
      </v-col>
    </v-row>
  </section>
</template>

<style scoped lang="scss">
.fr {
  padding: 48px 0;
}

.fr-eyebrow {
  font-size: 11.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgb(var(--v-theme-primary));
}

.fr-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.fr-title-ic {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

.fr-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
}

.fr-body {
  font-size: 15px;
  line-height: 1.65;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 14px;

  :deep(p) {
    margin-top: 12px;
  }

  :deep(p:first-child) {
    margin-top: 0;
  }
}

.fr-actions {
  margin-top: 22px;
}

.fr-media {
  /* breathing room so the tilted/glowing frame isn't clipped by the column */
  padding: 14px 6px;
}

@media (min-width: 960px) {
  .fr {
    padding: 72px 0;
  }

  .fr-title {
    font-size: 32px;
  }

  /* widen the gap between copy and the framed mockup (flip-aware) */
  .fr-copy {
    padding-right: 56px;
  }

  .fr-media {
    padding-left: 36px;
  }

  .flex-md-row-reverse {
    .fr-copy {
      padding-right: 0;
      padding-left: 56px;
    }

    .fr-media {
      padding-left: 6px;
      padding-right: 36px;
    }
  }
}
</style>
